import { ShieldCheck, HardHat, FileLock, Biohazard, IdCard, BadgeCheck, Radiation, Container, HeartPulse } from "lucide-react";
import { Reveal, Overline } from "@/components/Reveal";

const ITEMS = [
  { icon: ShieldCheck, title: "HIPAA Compliant", tag: "45 CFR PARTS 160/164", desc: "Encrypted digital manifest logs and strict driver confidentiality protocols." },
  { icon: HardHat, title: "OSHA 29 CFR 1910.1030", tag: "FEDERAL SAFETY", desc: "Mandated biological safety, spill containment kits, and biohazard isolation." },
  { icon: FileLock, title: "Medical Records Security", tag: "CONFIDENTIALITY", desc: "Tamper-evident physical envelopes and zero unauthorized disclosure." },
  { icon: Biohazard, title: "Bloodborne Pathogen Protocol", tag: "SAFETY CERTIFIED", desc: "Drivers rigorously trained and certified in BBP handling, containment, and disposal." },
  { icon: IdCard, title: "TWIC Verified Drivers", tag: "FEDERAL CLEARANCE", desc: "Transportation Worker Identification Credential for high-security installations." },
  { icon: BadgeCheck, title: "GDP Certified", tag: "GOOD DISTRIBUTION PRACTICE", desc: "Standardized global distribution practices protecting pharmaceutical integrity." },
  { icon: Radiation, title: "Chemotherapy Hazardous Drug Transportation", tag: "HAZMAT CERTIFIED", desc: "Certified cytotoxic and hazardous drug transport with spill-response trained couriers." },
  { icon: Container, title: "DOT HAZMAT Transportation", tag: "DOT REGULATED", desc: "Our specialized cryogenic medical courier service ensures temperature-sensitive specimens, biologics, and medical materials are transported safely, securely, and within required temperature controls." },
  { icon: HeartPulse, title: "CPR / AED / First-Aid", tag: "EMERGENCY RESPONSE", desc: "Every courier is certified in CPR, AED operation, and first-aid response — an added layer of safety on every route." },
];

export const Compliance = () => (
  <section id="compliance" data-testid="compliance-section" className="relative bg-[#0B1D4F] py-24 lg:py-32 overflow-hidden scroll-mt-20">
    <div className="absolute inset-0 bg-grid-light opacity-60" />
    <div className="absolute -top-40 left-1/3 h-[480px] w-[480px] rounded-full bg-[#0047FF]/25 blur-3xl" />

    <div className="relative mx-auto max-w-7xl px-5 sm:px-8">
      <Reveal className="max-w-2xl">
        <Overline light>Compliance &amp; Security Architecture</Overline>
        <h2 className="mt-5 font-display text-2xl sm:text-3xl lg:text-4xl font-bold tracking-tight text-white leading-tight">
          Certified at every layer of the chain.
        </h2>
        <p className="mt-5 text-base leading-relaxed text-slate-300">
          Barcode scanners, GPS tracking, mobile data and voice devices, specimen transport coolers,
          spill kits, and professional identification badges — standard on every route.
        </p>
      </Reveal>

      <div className="mt-14 grid sm:grid-cols-2 lg:grid-cols-3 gap-5">
        {ITEMS.map((c, i) => (
          <Reveal key={c.title} delay={i * 0.06}>
            <div data-testid={`compliance-card-${c.title.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}
                 className="group glass-dark rounded-2xl p-6 h-full hover:border-blue-400/40 transition-colors duration-300">
              <div className="flex items-start justify-between gap-4">
                <span className="h-11 w-11 rounded-xl bg-[#0047FF] flex items-center justify-center shrink-0 shadow-lg shadow-blue-600/30 group-hover:scale-105 transition-transform duration-300">
                  <c.icon className="h-5 w-5 text-white" />
                </span>
                <span className="font-mono text-[10px] font-semibold tracking-[0.14em] text-blue-300/80 border border-blue-400/25 rounded-lg px-2.5 py-1.5 text-right">
                  {c.tag}
                </span>
              </div>
              <h3 className="mt-5 font-display text-lg font-bold text-white tracking-tight">{c.title}</h3>
              <p className="mt-2 text-sm leading-relaxed text-slate-300">{c.desc}</p>
            </div>
          </Reveal>
        ))}
      </div>
    </div>
  </section>
);
